import {
  NavLink, Redirect, Route, useLocation, useParams,
} from 'react-router-dom';
import { Card, CardContent } from '@mui/material';
import React, { useContext, useEffect, useReducer } from 'react';
import { CardTab, CardTabs } from 'shared/components/styled/tabs';
import { getUser } from 'shared/api/admin';
import {
  useAdminRadCheck, useAuth, useRequest,
} from 'shared/hooks';
import { SideNavContext } from 'shared/hoc/withSideNav';
import { Title } from 'shared/components/abstract/interactables';
import profilePath, { profileRadPermissionsPath, profileTabGroupPath } from 'shared/routes/home';
import { dataReducer, DataReducerActions, initialDataReducerState } from 'shared/reducers';
import {
  getUserPath, getUserRadPermissionsPath, getUserTabGroupPath, viewPath,
} from 'shared/routes/admin';
import UserGroups from './UserGroupsTab';
import UserInfo from './UserInfoTab';
import RadiusPermissions from './RadiusPermissionsTab';

export default function User() {
  const syncSideNav = useContext(SideNavContext);
  const { request } = useRequest();
  const { routeId } = useParams();
  const { user: currentLoggedInUser, isAdmin } = useAuth();
  const userId = Number(routeId) || currentLoggedInUser.id;

  const [state, dispatch] = useReducer(dataReducer, initialDataReducerState);
  const { data: user, updatedData } = state;
  const { radCheck } = useAdminRadCheck(user?.username);

  const location = useLocation();
  const isProfile = location.pathname.startsWith('/profile');
  const tabRoutes = [
    { label: 'Details', Component: UserInfo },
    { label: 'Groups', Component: UserGroups },
  ];
  if (isProfile) {
    tabRoutes.find((tr) => tr.label === 'Details').path = profilePath;
    tabRoutes.find((tr) => tr.label === 'Groups').path = profileTabGroupPath;

    // Only display the radius permissions tab if user has radius credentials (RadCheck).
    if (radCheck?.id) {
      tabRoutes.push({ label: 'Radius Permissions', Component: RadiusPermissions, path: profileRadPermissionsPath });
    }
  } else {
    tabRoutes.find((tr) => tr.label === 'Details').path = getUserPath(routeId);
    tabRoutes.find((tr) => tr.label === 'Groups').path = getUserTabGroupPath(routeId);
    if (isAdmin) {
      tabRoutes.push({ label: 'Radius Permissions', Component: RadiusPermissions, path: getUserRadPermissionsPath(routeId) });
    }
  }

  const tabs = tabRoutes.map((tr) => (
    <CardTab
      key={tr.path}
      value={tr.path}
      to={tr.path}
      pathname={tr.path}
      component={NavLink}
      label={tr.label}
    />
  ));
  const routes = tabRoutes.map(({ path, Component }) => (
    <Route
      key={path}
      path={path}
      exact
      render={() => (
        <Component
          state={state}
          routeId={userId}
          isProfile={isProfile}
          dispatch={dispatch}
          updatedDate={updatedData}
          syncSideNav={syncSideNav}
        />
      )}
    />
  ));

  useEffect(() => {
    (async () => {
      const userResult = await request(getUser(userId));
      if (userResult === undefined) return;
      dispatch({ action: DataReducerActions.set, payload: userResult });
    })();
  }, [request, userId]);

  useEffect(() => {
    if (!user) return;
    syncSideNav(getUserPath(user.employeeId), user.username);
  }, [syncSideNav, user]);

  if (user === undefined || tabs.length === 0) return null;

  if (user === null) return <Redirect to={viewPath} />;

  return (
    <>
      <Title title={user.fullname} />
      <Card sx={{ maxWidth: 900 }}>
        <CardTabs
          value={location.pathname}
          variant="scrollable"
          allowScrollButtonsMobile
        >
          {tabs}
        </CardTabs>
        <CardContent sx={(theme) => ({
          bgcolor: (theme.vars || theme).palette.cardBackground,
        })}
        >
          {routes}
        </CardContent>
      </Card>
    </>
  );
}
